import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import {
  notifyPendingApprovalsUpdated,
  usePendingApprovalsCount,
} from '../hooks/usePendingApprovalsCount';
import {
  appConfigService,
  ContentGuidelines,
} from '../services/appConfigService';
import { supabase } from '../services/supabase';
import { Permission, hasAnyPermission } from '../utils/roles';

type PendingWallPost = {
  id: string;
  content?: string | null;
  author_name?: string | null;
  created_at: string;
  content_warning?: string | null;
  images?: unknown;
  video_url?: string | null;
};

type PendingWallComment = {
  id: string;
  content?: string | null;
  author_name?: string | null;
  created_at: string;
};

type MessageState = {
  type: 'success' | 'error';
  text: string;
} | null;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const LoadingText = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  padding: 1.5rem 0;
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
`;

const CardTitle = styled.h2`
  margin: 0 0 0.25rem;
  font-size: 1.2rem;
  color: var(--onSurface);
`;

const CardSubtitle = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.95rem;
`;

const Message = styled.div<{ $type: 'success' | 'error' }>`
  border-radius: 12px;
  padding: 0.85rem 1rem;
  font-size: 0.95rem;
  border: 1px solid ${({ $type }) => ($type === 'success' ? 'var(--primary)' : 'var(--error)')};
  background: ${({ $type }) => ($type === 'success' ? 'rgba(76, 175, 80, 0.12)' : 'rgba(244, 67, 54, 0.12)')};
  color: ${({ $type }) => ($type === 'success' ? 'var(--onSurface)' : 'var(--error)')};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-top: 1rem;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 240px) minmax(0, 1fr);
  gap: 0.85rem;
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  color: var(--onSurface);
  font-weight: 600;
`;

const TextInput = styled.input`
  width: 100%;
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.7rem 0.8rem;
  font: inherit;
  color: var(--onSurface);
  background: var(--surfaceVariant);
`;

const Toggle = styled.input`
  justify-self: start;
  width: 18px;
  height: 18px;
  accent-color: var(--primary);
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid var(--outlineVariant);
  margin: 1rem 0 0;
`;

const SaveButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 0.8rem 1rem;
  margin-top: 1rem;
  font: inherit;
  font-weight: 600;
  background: var(--primary);
  color: var(--onPrimary);
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SmallText = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
`;

const EmptyState = styled.p`
  margin: 0;
  padding: 1rem 0;
  color: var(--onSurfaceVariant);
`;

const PendingItem = styled.div`
  border: 1px solid var(--outlineVariant);
  border-radius: 12px;
  padding: 0.95rem;
  background: var(--surfaceVariant);
`;

const PendingMeta = styled(SmallText)`
  margin-top: 0.5rem;
`;

const PendingMediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(84px, 120px));
  gap: 0.6rem;
  margin-top: 0.75rem;
`;

const PendingMediaImage = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 10px;
  cursor: pointer;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
`;

const ApproveButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
  font: inherit;
  font-weight: 600;
  background: var(--primary);
  color: var(--onPrimary);
  cursor: pointer;
`;

const DangerButton = styled.button`
  border: 1px solid var(--error);
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
  font: inherit;
  font-weight: 600;
  background: transparent;
  color: var(--error);
  cursor: pointer;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 26px;
  padding: 0 0.55rem;
  border-radius: 999px;
  background: var(--error);
  color: var(--onError);
  font-size: 0.85rem;
  font-weight: 700;
`;

const ExternalLink = styled.a`
  color: var(--primary);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.62);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1200;
`;

const ModalCard = styled(Card)`
  width: min(900px, 100%);
  max-height: 90vh;
  overflow: auto;
`;

const PreviewMedia = styled.img`
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 12px;
`;

const PreviewVideo = styled.video`
  width: 100%;
  max-height: 70vh;
  border-radius: 12px;
`;

const toNumber = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const listToString = (values?: string[]) => (values && values.length ? values.join(', ') : '');

const stringToList = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeImageList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string' && !!item);
  if (typeof value === 'string' && value.length) {
    return value
      .replace(/[{}[\]"]+/g, '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeVideoUrl = (value?: string | null) => {
  if (!value) return '';
  return value.trim();
};

export default function ContentApprovalAdminPage() {
  const { user } = useAuth();
  const { data: profile, isLoading } = useCurrentUserProfile(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canManageContent = hasAnyPermission(roles, [Permission.MANAGE_CONTENT]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contentGuidelines, setContentGuidelines] = useState<ContentGuidelines | null>(null);
  const [pendingContentLoading, setPendingContentLoading] = useState(false);
  const [pendingContentError, setPendingContentError] = useState<string | null>(null);
  const [pendingWallPosts, setPendingWallPosts] = useState<PendingWallPost[]>([]);
  const [pendingWallComments, setPendingWallComments] = useState<PendingWallComment[]>([]);
  const [message, setMessage] = useState<MessageState>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState<'image' | 'video'>('image');
  const [previewUrl, setPreviewUrl] = useState('');
  const { count: pendingApprovalsCount, refresh: refreshPendingApprovalsCount } =
    usePendingApprovalsCount(canManageContent);

  const showMessage = useCallback((type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    window.setTimeout(() => setMessage(null), 3000);
  }, []);

  const loadPendingContent = useCallback(async () => {
    setPendingContentLoading(true);
    setPendingContentError(null);
    try {
      const [wallPostsRes, wallCommentsRes] = await Promise.all([
        supabase
          .from('wall_posts')
          .select('id, content, author_name, created_at, content_warning, images, video_url')
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(50),
        supabase
          .from('wall_comments')
          .select('id, content, author_name, created_at')
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(50),
      ]);

      if (wallPostsRes.error || wallCommentsRes.error) {
        setPendingContentError('Failed to load pending approvals.');
        return;
      }

      setPendingWallPosts((wallPostsRes.data as PendingWallPost[]) ?? []);
      setPendingWallComments((wallCommentsRes.data as PendingWallComment[]) ?? []);
    } catch (error) {
      setPendingContentError('Failed to load pending approvals.');
    } finally {
      setPendingContentLoading(false);
    }
  }, []);

  const loadPageData = useCallback(async () => {
    setLoading(true);
    try {
      const [contentGuidelinesResult] = await Promise.all([
        appConfigService.getContentGuidelines(),
        loadPendingContent(),
      ]);

      if (contentGuidelinesResult.success && contentGuidelinesResult.data) {
        setContentGuidelines(contentGuidelinesResult.data);
      }
    } finally {
      setLoading(false);
    }
  }, [loadPendingContent]);

  const updatePendingStatus = useCallback(
    async (table: 'wall_posts' | 'wall_comments', id: string, status: 'published' | 'rejected') => {
      try {
        let finalStatus: string = status;
        const updates: Record<string, unknown> = {};
        if (table === 'wall_posts' && status === 'published') {
          const { data: postRow } = await supabase
            .from('wall_posts')
            .select('scheduled_for')
            .eq('id', id)
            .maybeSingle();
          const scheduledForIso = postRow?.scheduled_for ?? null;
          const scheduledFor = scheduledForIso ? new Date(scheduledForIso) : null;
          // A future scheduled_for means approval unlocks the post for the dispatcher, not immediate publish.
          if (scheduledFor && scheduledFor.getTime() > Date.now()) {
            finalStatus = 'scheduled';
          } else if (scheduledForIso) {
            updates.created_at = scheduledForIso;
            updates.published_at = new Date().toISOString();
          }
        }
        updates.status = finalStatus;

        const { error } = await supabase.from(table).update(updates).eq('id', id);
        if (error) {
          showMessage('error', 'Failed to update content status.');
          return;
        }

        showMessage('success', status === 'published' ? 'Approved successfully.' : 'Rejected successfully.');
        await loadPendingContent();
        refreshPendingApprovalsCount();
        notifyPendingApprovalsUpdated();
      } catch (error) {
        showMessage('error', 'Failed to update content status.');
      }
    },
    [loadPendingContent, refreshPendingApprovalsCount, showMessage]
  );

  const handleSave = useCallback(async () => {
    if (!contentGuidelines) return;
    setSaving(true);
    try {
      const result = await appConfigService.updateContentGuidelines(contentGuidelines);
      showMessage(result.success ? 'success' : 'error', result.success ? 'Saved successfully.' : 'Save failed.');
    } catch (error) {
      showMessage('error', 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [contentGuidelines, showMessage]);

  const openPreview = useCallback((type: 'image' | 'video', url?: string | null) => {
    if (!url) return;
    setPreviewType(type);
    setPreviewUrl(url);
    setPreviewOpen(true);
  }, []);

  useEffect(() => {
    if (!canManageContent) return;
    loadPageData();
  }, [canManageContent, loadPageData]);

  if (isLoading || loading) {
    return (
      <Page>
        <LoadingText>Loading</LoadingText>
      </Page>
    );
  }

  if (!canManageContent) {
    return (
      <Page>
        <Card>
          <CardTitle>Access Denied</CardTitle>
          <CardSubtitle>You do not have permission to manage content approvals.</CardSubtitle>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <HeaderRow>
        <PageTitle>Content Approval</PageTitle>
        {pendingApprovalsCount > 0 ? <CountBadge>{pendingApprovalsCount}</CountBadge> : null}
      </HeaderRow>

      {message ? <Message $type={message.type}>{message.text}</Message> : null}

      {previewOpen ? (
        <ModalOverlay onClick={() => setPreviewOpen(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <HeaderRow>
              <CardTitle style={{ marginBottom: 0 }}>Preview</CardTitle>
              <DangerButton type="button" onClick={() => setPreviewOpen(false)}>
                Close
              </DangerButton>
            </HeaderRow>
            <Divider />
            {previewType === 'image' ? (
              <PreviewMedia src={previewUrl} alt="Pending preview" />
            ) : (
              <PreviewVideo src={previewUrl} controls />
            )}
          </ModalCard>
        </ModalOverlay>
      ) : null}

      {contentGuidelines ? (
        <Card>
          <CardTitle>Content Guidelines</CardTitle>
          <CardSubtitle>Content moderation and posting guidelines.</CardSubtitle>
          <Section>
            <FieldRow>
              <Label>Max Post Length</Label>
              <TextInput
                value={contentGuidelines.max_post_length.toString()}
                onChange={(event) =>
                  setContentGuidelines({
                    ...contentGuidelines,
                    max_post_length: toNumber(event.target.value),
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Max Comment Length</Label>
              <TextInput
                value={contentGuidelines.max_comment_length.toString()}
                onChange={(event) =>
                  setContentGuidelines({
                    ...contentGuidelines,
                    max_comment_length: toNumber(event.target.value),
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Allowed Media Types</Label>
              <TextInput
                value={listToString(contentGuidelines.allowed_media_types)}
                onChange={(event) =>
                  setContentGuidelines({
                    ...contentGuidelines,
                    allowed_media_types: stringToList(event.target.value),
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Max Media Per Post</Label>
              <TextInput
                value={contentGuidelines.max_media_per_post.toString()}
                onChange={(event) =>
                  setContentGuidelines({
                    ...contentGuidelines,
                    max_media_per_post: toNumber(event.target.value),
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Profanity Filter</Label>
              <Toggle
                type="checkbox"
                checked={contentGuidelines.profanity_filter_enabled}
                onChange={(event) =>
                  setContentGuidelines({
                    ...contentGuidelines,
                    profanity_filter_enabled: event.target.checked,
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Auto Moderation</Label>
              <Toggle
                type="checkbox"
                checked={contentGuidelines.auto_moderation_enabled}
                onChange={(event) =>
                  setContentGuidelines({
                    ...contentGuidelines,
                    auto_moderation_enabled: event.target.checked,
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Require Approval for Posts</Label>
              <Toggle
                type="checkbox"
                checked={contentGuidelines.require_approval_for_posts}
                onChange={(event) =>
                  setContentGuidelines({
                    ...contentGuidelines,
                    require_approval_for_posts: event.target.checked,
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Require Approval for Comments</Label>
              <Toggle
                type="checkbox"
                checked={contentGuidelines.require_approval_for_comments}
                onChange={(event) =>
                  setContentGuidelines({
                    ...contentGuidelines,
                    require_approval_for_comments: event.target.checked,
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Content Warning Required</Label>
              <Toggle
                type="checkbox"
                checked={contentGuidelines.content_warning_required}
                onChange={(event) =>
                  setContentGuidelines({
                    ...contentGuidelines,
                    content_warning_required: event.target.checked,
                  })
                }
              />
            </FieldRow>
          </Section>
          <Divider />
          <SaveButton type="button" disabled={saving} onClick={handleSave}>
            Save Content Guidelines
          </SaveButton>
        </Card>
      ) : null}

      <Card>
        <CardTitle>Pending Approvals</CardTitle>
        <CardSubtitle>Review pending posts and comments.</CardSubtitle>
        <Section>
          {pendingContentLoading ? <SmallText>Loading pending items</SmallText> : null}
          {pendingContentError ? <SmallText>{pendingContentError}</SmallText> : null}
          {!pendingContentLoading &&
          !pendingContentError &&
          pendingWallPosts.length === 0 &&
          pendingWallComments.length === 0 ? (
            <EmptyState>No pending posts or comments.</EmptyState>
          ) : null}

          {pendingWallPosts.length > 0 ? (
            <Section>
              <Label>Wall Posts</Label>
              {pendingWallPosts.map((item) => (
                <PendingItem key={item.id}>
                  <div>{item.content?.slice(0, 160) || 'No content'}</div>
                  {item.content_warning ? <PendingMeta>Warning: {item.content_warning}</PendingMeta> : null}
                  {normalizeImageList(item.images).length > 0 ? (
                    <PendingMediaGrid>
                      {normalizeImageList(item.images).map((src, index) => (
                        <PendingMediaImage
                          key={`${item.id}-image-${index}`}
                          src={src}
                          alt="Pending"
                          onClick={() => openPreview('image', src)}
                        />
                      ))}
                    </PendingMediaGrid>
                  ) : null}
                  {normalizeVideoUrl(item.video_url) ? (
                    <PendingMeta>
                      Video:{' '}
                      <ExternalLink
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          openPreview('video', normalizeVideoUrl(item.video_url));
                        }}
                      >
                        Preview
                      </ExternalLink>
                    </PendingMeta>
                  ) : null}
                  <PendingMeta>
                    {item.author_name || 'Unknown'} {new Date(item.created_at).toLocaleString()}
                  </PendingMeta>
                  <ActionRow>
                    <ApproveButton
                      type="button"
                      onClick={() => updatePendingStatus('wall_posts', item.id, 'published')}
                    >
                      Approve
                    </ApproveButton>
                    <DangerButton
                      type="button"
                      onClick={() => updatePendingStatus('wall_posts', item.id, 'rejected')}
                    >
                      Reject
                    </DangerButton>
                  </ActionRow>
                </PendingItem>
              ))}
            </Section>
          ) : null}

          {pendingWallComments.length > 0 ? (
            <Section>
              <Label>Wall Comments</Label>
              {pendingWallComments.map((item) => (
                <PendingItem key={item.id}>
                  <div>{item.content?.slice(0, 160) || 'No content'}</div>
                  <PendingMeta>
                    {item.author_name || 'Unknown'} {new Date(item.created_at).toLocaleString()}
                  </PendingMeta>
                  <ActionRow>
                    <ApproveButton
                      type="button"
                      onClick={() => updatePendingStatus('wall_comments', item.id, 'published')}
                    >
                      Approve
                    </ApproveButton>
                    <DangerButton
                      type="button"
                      onClick={() => updatePendingStatus('wall_comments', item.id, 'rejected')}
                    >
                      Reject
                    </DangerButton>
                  </ActionRow>
                </PendingItem>
              ))}
            </Section>
          ) : null}
        </Section>
      </Card>
    </Page>
  );
}
